/**
 * SalesChart Component
 *
 * Interactive scatter plot showing sales history, listings, and price trends.
 * Uses Chart.js for rendering.
 */
import { Chart, ScatterController, LineController, PointElement, LineElement, LinearScale, TimeScale, CategoryScale, Tooltip, Legend, } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { CHART_COLORS, TIME_PERIODS, PRICE_BANDS, AUTO_TOTALS_THRESHOLD, PROPERTY_TYPE_COLORS, PROPERTY_TYPE_LABELS, } from './types.js';
import { filterSales, filterListings, calculateStats, calculateLinearRegression, getYValue, getNegotiationPercent, getUniqueBedrooms, formatPrice, formatDate, getDateCutoff, bucketByQuarter, bucketByMonth, calculatePropertyTypeBreakdown, computeYCeiling, hexToRgba, } from './utils.js';
import { injectStyles } from './styles.js';
// Register Chart.js components
Chart.register(ScatterController, LineController, PointElement, LineElement, LinearScale, TimeScale, CategoryScale, Tooltip, Legend);
export class SalesChart {
    options;
    data;
    filters;
    chart = null;
    container;
    showPropertyTypeFilter;
    constructor(options) {
        this.options = options;
        this.data = options.data;
        this.container = options.container;
        this.showPropertyTypeFilter = options.showPropertyTypeFilter ?? true;
        this.filters = {
            timePeriod: options.initialFilters?.timePeriod || '10y',
            bedrooms: options.initialFilters?.bedrooms || 'all',
            chartMode: options.initialFilters?.chartMode || 'price',
            groupBy: options.initialFilters?.groupBy || this.computeDefaultGroupBy(options.data),
            priceBand: options.initialFilters?.priceBand ?? 'all',
            propertyType: options.initialFilters?.propertyType || 'all',
            summaryMetric: options.initialFilters?.summaryMetric || 'unitsSold',
        };
        injectStyles();
        this.render();
    }
    /**
     * Dense scopes (busy districts/regions, or a handful of very active
     * neighborhoods) default to the aggregated Summary view since a scatter plot
     * with hundreds of overlapping dots just reads as a solid mass of color -
     * see AUTO_TOTALS_THRESHOLD's comment for how this number was picked.
     * Only runs at construction time (a fresh scope) - an explicit caller
     * override via initialFilters.groupBy always wins, and a later
     * updateData() call deliberately does NOT re-run this, so a user's
     * manual toggle sticks for the lifetime of this chart instance.
     */
    computeDefaultGroupBy(data) {
        return data.sales.length + data.listings.length > AUTO_TOTALS_THRESHOLD ? 'summary' : 'none';
    }
    /**
     * Update chart data
     */
    updateData(data) {
        this.data = data;
        this.updateChart();
        this.updateStats();
        this.updateFilterButtons();
    }
    /**
     * Destroy the component
     */
    destroy() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
        this.container.innerHTML = '';
    }
    render() {
        this.container.innerHTML = `
      <div class="sm-sales-chart">
        <!-- Stats Row -->
        <div class="sm-sales-chart-stats-wrap" id="sales-chart-stats">
          ${this.buildStatsHtml()}
        </div>

        <!-- Filters -->
        <div class="sm-sales-chart-filters">
          <!-- Time Period -->
          <div class="sm-sales-chart-filter-row">
            <span class="sm-sales-chart-filter-label">Time Period</span>
            <div class="sm-sales-chart-btn-group" id="time-period-btns">
              ${this.buildTimePeriodButtons()}
            </div>
          </div>

          <!-- Bedrooms -->
          <div class="sm-sales-chart-filter-row">
            <span class="sm-sales-chart-filter-label">Bedrooms</span>
            <div class="sm-sales-chart-btn-group" id="bedrooms-btns">
              ${this.buildBedroomsButtons()}
            </div>
          </div>

          <!-- Property Type - percentages (and the dot colors, which
               double as the Summary stacked-area legend) - see
               isPropertyTypeBreakdownAvailable/classifyPropertyType for how
               House/Condo stay well-covered at every TimePeriod via the
               parcel-level TMK fallback. No Land option - see
               PropertyTypeFilterValue's comment in types.ts. Hidden
               entirely at neighborhood scope - see showPropertyTypeFilter's
               comment in types.ts. -->
          ${this.showPropertyTypeFilter ? `
          <div class="sm-sales-chart-filter-row">
            <span class="sm-sales-chart-filter-label">Property Type</span>
            <div class="sm-sales-chart-btn-group" id="property-type-btns">
              ${this.buildPropertyTypeButtons()}
            </div>
          </div>
          ` : ''}

          <!-- Price Band - a filter (like Bedrooms/Property Type), not a
               view mode, so it composes with the View toggle below:
               "Summary" + "$1-2M" shows sold counts over time for just
               that band. -->
          <div class="sm-sales-chart-filter-row">
            <span class="sm-sales-chart-filter-label">Price Band</span>
            <div class="sm-sales-chart-btn-group" id="price-band-btns">
              ${this.buildPriceBandButtons()}
            </div>
          </div>

          <!-- View: individual points vs. aggregated totals (month or year
               grain, chosen by timePeriod - see summaryGrainFor). -->
          <div class="sm-sales-chart-filter-row">
            <span class="sm-sales-chart-filter-label">View</span>
            <div class="sm-sales-chart-mode-toggle" id="group-by-toggle">
              <button class="sm-sales-chart-mode-btn${this.filters.groupBy === 'none' ? ' active' : ''}" data-groupby="none">
                Details
              </button>
              <button class="sm-sales-chart-mode-btn${this.filters.groupBy === 'summary' ? ' active' : ''}" data-groupby="summary">
                Summary
              </button>
            </div>
          </div>

          <!-- Chart Mode Toggle - meaningless once Summary shows counts
               instead of prices, so hidden whenever grouped (see
               setGroupBy). $/Sq Ft
               itself is disabled outside 6m/1y - see isPricePerSqftAvailable's
               comment for why the data just isn't there at longer windows. -->
          <div class="sm-sales-chart-filter-row" id="chart-mode-row"${this.filters.groupBy !== 'none' ? ' style="display:none"' : ''}>
            <span class="sm-sales-chart-filter-label">Y-Axis</span>
            <div class="sm-sales-chart-mode-toggle" id="chart-mode-toggle">
              <button class="sm-sales-chart-mode-btn${this.filters.chartMode === 'price' ? ' active' : ''}" data-mode="price">
                Price
              </button>
              <button class="sm-sales-chart-mode-btn${this.filters.chartMode === 'pricePerSqft' ? ' active' : ''}"${this.isPricePerSqftAvailable() ? '' : ' disabled title="Square footage coverage is too thin beyond 1 year - switch to 6mo/1yr to use this"'} data-mode="pricePerSqft">
                $/Sq Ft
              </button>
            </div>
          </div>

          <!-- Summary's own Y-axis - mirrors chart-mode-row above but only
               shown in Summary view (see setGroupBy). Units Sold can stack
               by property type (chart-mode-row's Price/$-per-Sq-Ft can't
               meaningfully stack at all); Median Price can't stack across
               types - medians don't sum - so it's always a single line. -->
          <div class="sm-sales-chart-filter-row" id="summary-metric-row"${this.filters.groupBy !== 'summary' ? ' style="display:none"' : ''}>
            <span class="sm-sales-chart-filter-label">Y-Axis</span>
            <div class="sm-sales-chart-mode-toggle" id="summary-metric-toggle">
              <button class="sm-sales-chart-mode-btn${this.filters.summaryMetric === 'unitsSold' ? ' active' : ''}" data-metric="unitsSold">
                Units Sold
              </button>
              <button class="sm-sales-chart-mode-btn${this.filters.summaryMetric === 'medianPrice' ? ' active' : ''}" data-metric="medianPrice">
                Median Price
              </button>
            </div>
          </div>
        </div>

        <!-- Chart Canvas -->
        <div class="sm-sales-chart-canvas-container">
          <canvas id="sales-chart-canvas"></canvas>
        </div>

        <!-- Legend -->
        <div class="sm-sales-chart-legend">
          <div class="sm-sales-chart-legend-item">
            <span class="sm-sales-chart-legend-dot sold"></span>
            <span>Sold</span>
          </div>
          <!-- For Sale/Pending are a *current* snapshot, not a historical
               total - misleading in Summary's line view (see
               prepareSummaryData's comment), so hidden there. -->
          <div class="sm-sales-chart-legend-item" id="for-sale-legend-item"${this.filters.groupBy !== 'none' ? ' style="display:none"' : ''}>
            <span class="sm-sales-chart-legend-dot for-sale"></span>
            <span>For Sale</span>
          </div>
          <div class="sm-sales-chart-legend-item" id="pending-legend-item"${this.filters.groupBy !== 'none' ? ' style="display:none"' : ''}>
            <span class="sm-sales-chart-legend-dot pending"></span>
            <span>Pending</span>
          </div>
          <!-- Trend line only exists in the scatter view. -->
          <div class="sm-sales-chart-legend-item" id="trend-legend-item"${this.filters.groupBy !== 'none' ? ' style="display:none"' : ''}>
            <span class="sm-sales-chart-legend-line"></span>
            <span>Trend</span>
          </div>
        </div>

        <!-- Summary's trailing bucket (this month/year/quarter) is always
             partial - today is never the last day of a period - see
             prepareSummaryData. Only relevant in Summary mode. -->
        <div class="sm-sales-chart-partial-note" id="partial-period-note"${this.filters.groupBy === 'none' ? ' style="display:none"' : ''}>
          * Partial period
        </div>

        <!-- Details-only - populated/shown by updateClipNote whenever the
             Y-axis ceiling (see computeYCeiling) actually clips a point.
             Empty/hidden otherwise, including in Summary mode. -->
        <div class="sm-sales-chart-partial-note" id="clip-note" style="display:none"></div>
      </div>
    `;
        this.attachEventListeners();
        this.createChart();
    }
    buildStatsHtml() {
        const filteredSales = filterSales(this.data.sales, this.filters);
        const stats = calculateStats(filteredSales);
        return `
      <div class="sm-sales-chart-stats">
        <div class="sm-sales-chart-stat">
          <div class="sm-sales-chart-stat-value">${stats.salesCount}</div>
          <div class="sm-sales-chart-stat-label">Sales</div>
        </div>
        <div class="sm-sales-chart-stat">
          <div class="sm-sales-chart-stat-value">${formatPrice(stats.avgSalePrice)}</div>
          <div class="sm-sales-chart-stat-label">Avg Sale Price</div>
        </div>
        <div class="sm-sales-chart-stat">
          <div class="sm-sales-chart-stat-value">${stats.closeRatio}%</div>
          <div class="sm-sales-chart-stat-label">Close Ratio</div>
        </div>
        <div class="sm-sales-chart-stat">
          <div class="sm-sales-chart-stat-value">${stats.avgDaysOnMarket ?? 'N/A'}</div>
          <div class="sm-sales-chart-stat-label">Avg DOM</div>
        </div>
      </div>
    `;
    }
    // Used to gate the breakdown's *percentage* annotation (and Summary's
    // stacked-area coloring). Historically this returned false beyond 6m/1y,
    // since MLS's property_type/property_subtype has a hard coverage cliff
    // past roughly the trailing 6 months (see classifyPropertyType's comment
    // in utils.ts). classifyPropertyType now falls back to the parcel-level
    // tmkPropertyGroup for House/Condo, which has no such cliff (~91% overall
    // coverage, confirmed live 2026-10), so the breakdown is meaningful at
    // every TimePeriod now.
    isPropertyTypeBreakdownAvailable() {
        return true;
    }
    // Doubles as both the Property Type filter and (via the dot colors) the
    // Summary stacked-area legend - merged from what used to be two separate
    // things (a plain breakdown display, and no filter at all). Percentages
    // are computed with propertyType forced back to 'all', so picking
    // "Condo" doesn't make every remaining button read "100%" - they always
    // describe the type mix of the current view, independent of which one
    // (if any) is currently selected.
    buildPropertyTypeButtons() {
        const breakdownFilters = { ...this.filters, propertyType: 'all' };
        const salesForBreakdown = filterSales(this.data.sales, breakdownFilters);
        const breakdown = this.isPropertyTypeBreakdownAvailable()
            ? calculatePropertyTypeBreakdown(salesForBreakdown)
            : null;
        const pctSuffix = (key) => {
            if (!breakdown || breakdown.total === 0)
                return '';
            return ` ${Math.round((breakdown[key] / breakdown.total) * 100)}%`;
        };
        const dot = (key) => `<span class="sm-sales-chart-type-dot" style="background:${PROPERTY_TYPE_COLORS[key]}"></span>`;
        const typeButton = (key, label) => `
      <button class="sm-sales-chart-btn${this.filters.propertyType === key ? ' active' : ''}" data-propertytype="${key}">
        ${dot(key)}${label}${pctSuffix(key)}
      </button>
    `;
        return [
            `<button class="sm-sales-chart-btn${this.filters.propertyType === 'all' ? ' active' : ''}" data-propertytype="all">All</button>`,
            typeButton('house', 'House'),
            typeButton('condo', 'Condo'),
        ].join('');
    }
    buildTimePeriodButtons() {
        return Object.entries(TIME_PERIODS)
            .map(([key, { label }]) => `
        <button
          class="sm-sales-chart-btn${this.filters.timePeriod === key ? ' active' : ''}"
          data-period="${key}"
        >
          ${label}
        </button>
      `)
            .join('');
    }
    buildBedroomsButtons() {
        const bedrooms = getUniqueBedrooms(this.data);
        const buttons = [
            `<button class="sm-sales-chart-btn${this.filters.bedrooms === 'all' ? ' active' : ''}" data-bedrooms="all">All</button>`,
        ];
        for (const br of bedrooms) {
            buttons.push(`
        <button
          class="sm-sales-chart-btn${this.filters.bedrooms === br ? ' active' : ''}"
          data-bedrooms="${br}"
        >
          ${br === 0 ? 'Studio' : br === '5+' ? '5+ BR' : `${br} BR`}
        </button>
      `);
        }
        return buttons.join('');
    }
    buildPriceBandButtons() {
        const buttons = [
            `<button class="sm-sales-chart-btn${this.filters.priceBand === 'all' ? ' active' : ''}" data-priceband="all">All</button>`,
        ];
        PRICE_BANDS.forEach((band, i) => {
            buttons.push(`
        <button
          class="sm-sales-chart-btn${this.filters.priceBand === i ? ' active' : ''}"
          data-priceband="${i}"
        >
          ${band.label}
        </button>
      `);
        });
        return buttons.join('');
    }
    updateStats() {
        const statsEl = this.container.querySelector('#sales-chart-stats');
        if (statsEl) {
            statsEl.innerHTML = this.buildStatsHtml();
        }
    }
    // Property Type's percentages depend on timePeriod/bedrooms/priceBand
    // (whatever's narrowing the sales set the breakdown is computed over),
    // so unlike bedrooms/price-band's buttons (which only need their
    // .active class toggled), this row's HTML is rebuilt on every filter
    // change - see setFilter.
    updatePropertyTypeButtons() {
        const container = this.container.querySelector('#property-type-btns');
        if (!container)
            return;
        container.innerHTML = this.buildPropertyTypeButtons();
        this.attachPropertyTypeListeners();
    }
    updateFilterButtons() {
        // Update bedrooms
        const bedroomsBtns = this.container.querySelector('#bedrooms-btns');
        if (bedroomsBtns) {
            bedroomsBtns.innerHTML = this.buildBedroomsButtons();
            this.attachBedroomsListeners();
        }
        this.updatePropertyTypeButtons();
    }
    attachEventListeners() {
        // Time period buttons
        const timePeriodBtns = this.container.querySelectorAll('#time-period-btns .sm-sales-chart-btn');
        timePeriodBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const period = btn.dataset.period;
                this.setFilter('timePeriod', period);
                this.updateButtonActive(timePeriodBtns, btn);
                this.updateChartModeAvailability();
            });
        });
        // Bedrooms buttons
        this.attachBedroomsListeners();
        // Property type buttons
        this.attachPropertyTypeListeners();
        // Price band buttons
        const priceBandBtns = this.container.querySelectorAll('#price-band-btns .sm-sales-chart-btn');
        priceBandBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const value = btn.dataset.priceband;
                const priceBand = value === 'all' ? 'all' : parseInt(value, 10);
                this.setFilter('priceBand', priceBand);
                this.updateButtonActive(priceBandBtns, btn);
            });
        });
        // Chart mode toggle
        const modeBtns = this.container.querySelectorAll('#chart-mode-toggle .sm-sales-chart-mode-btn');
        modeBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.dataset.mode;
                this.setFilter('chartMode', mode);
                this.updateButtonActive(modeBtns, btn);
            });
        });
        // Summary metric toggle (Units Sold / Median Price)
        const summaryMetricBtns = this.container.querySelectorAll('#summary-metric-toggle .sm-sales-chart-mode-btn');
        summaryMetricBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const metric = btn.dataset.metric;
                this.setFilter('summaryMetric', metric);
                this.updateButtonActive(summaryMetricBtns, btn);
            });
        });
        // Group-by (view) toggle
        const groupByBtns = this.container.querySelectorAll('#group-by-toggle .sm-sales-chart-mode-btn');
        groupByBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const groupBy = btn.dataset.groupby;
                this.setGroupBy(groupBy);
                this.updateButtonActive(groupByBtns, btn);
            });
        });
    }
    attachBedroomsListeners() {
        const bedroomsBtns = this.container.querySelectorAll('#bedrooms-btns .sm-sales-chart-btn');
        bedroomsBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const value = btn.dataset.bedrooms;
                const bedrooms = value === 'all' || value === '5+' ? value : parseInt(value, 10);
                this.setFilter('bedrooms', bedrooms);
                this.updateButtonActive(bedroomsBtns, btn);
            });
        });
    }
    // Re-attached after every rebuild of #property-type-btns (see
    // updatePropertyTypeButtons) - setFilter itself already rebuilds this
    // row with the correct .active class baked in, so there's no separate
    // updateButtonActive call needed here the way priceBand/chartMode have.
    attachPropertyTypeListeners() {
        const btns = this.container.querySelectorAll('#property-type-btns .sm-sales-chart-btn');
        btns.forEach(btn => {
            btn.addEventListener('click', () => {
                const value = btn.dataset.propertytype;
                this.setFilter('propertyType', value);
            });
        });
    }
    // $/Sq Ft coverage comes almost entirely from mls_listings.living_area,
    // which only exists for listings synced with full MLS detail in roughly
    // the last year - older sales (whether MLS-sourced stub rows from the
    // one-time historical events backfill, or county-recorded deed sales)
    // have essentially no sqft data at all (confirmed live: 0% coverage for
    // 2025 closings vs ~65% for 2026). Rather than show an almost-empty
    // chart at 5yr/10yr, the toggle is disabled outside the two windows
    // where the data actually exists.
    isPricePerSqftAvailable() {
        return this.filters.timePeriod === '6m' || this.filters.timePeriod === '1y';
    }
    // Patches the $/Sq Ft button's disabled state after a timePeriod change
    // (no full shell re-render on filter changes - see setFilter) and, if the
    // user was on $/Sq Ft when it becomes unavailable, falls back to Price
    // rather than leaving them stranded on a disabled, stale view.
    updateChartModeAvailability() {
        const sqftBtn = this.container.querySelector('#chart-mode-toggle [data-mode="pricePerSqft"]');
        if (!sqftBtn)
            return;
        const available = this.isPricePerSqftAvailable();
        sqftBtn.disabled = !available;
        if (available) {
            sqftBtn.removeAttribute('title');
        }
        else {
            sqftBtn.title = 'Square footage coverage is too thin beyond 1 year - switch to 6mo/1yr to use this';
        }
        if (!available && this.filters.chartMode === 'pricePerSqft') {
            this.setFilter('chartMode', 'price');
            const priceBtn = this.container.querySelector('#chart-mode-toggle [data-mode="price"]');
            const modeBtns = this.container.querySelectorAll('#chart-mode-toggle .sm-sales-chart-mode-btn');
            if (priceBtn)
                this.updateButtonActive(modeBtns, priceBtn);
        }
    }
    updateButtonActive(buttons, activeBtn) {
        buttons.forEach(b => b.classList.remove('active'));
        activeBtn.classList.add('active');
    }
    setFilter(key, value) {
        this.filters[key] = value;
        this.updateChart();
        this.updateStats();
        this.updatePropertyTypeButtons();
    }
    /**
     * Separate from setFilter() because switching groupBy changes the Chart.js
     * `type` itself (scatter vs. line) - .update() can't morph that in place,
     * so this destroys and recreates the chart instance instead. Also patches
     * the two bits of surrounding UI that only make sense in one mode (the
     * Y-Axis price/$-per-sqft toggle, the Trend legend swatch).
     */
    setGroupBy(value) {
        this.filters.groupBy = value;
        const chartModeRow = this.container.querySelector('#chart-mode-row');
        if (chartModeRow)
            chartModeRow.style.display = value !== 'none' ? 'none' : '';
        const summaryMetricRow = this.container.querySelector('#summary-metric-row');
        if (summaryMetricRow)
            summaryMetricRow.style.display = value === 'summary' ? '' : 'none';
        const trendLegendItem = this.container.querySelector('#trend-legend-item');
        if (trendLegendItem)
            trendLegendItem.style.display = value !== 'none' ? 'none' : '';
        const forSaleLegendItem = this.container.querySelector('#for-sale-legend-item');
        if (forSaleLegendItem)
            forSaleLegendItem.style.display = value !== 'none' ? 'none' : '';
        const pendingLegendItem = this.container.querySelector('#pending-legend-item');
        if (pendingLegendItem)
            pendingLegendItem.style.display = value !== 'none' ? 'none' : '';
        const partialNote = this.container.querySelector('#partial-period-note');
        if (partialNote)
            partialNote.style.display = value === 'none' ? 'none' : '';
        // Switching into Summary mode skips updateClipNote entirely (it's only
        // called from the scatter path) - hide any note left over from Details
        // explicitly rather than leaving its last text/visibility stale.
        if (value !== 'none') {
            const clipNote = this.container.querySelector('#clip-note');
            if (clipNote)
                clipNote.style.display = 'none';
        }
        this.chart?.destroy();
        this.chart = null;
        this.createChart();
    }
    createChart() {
        if (this.filters.groupBy !== 'none') {
            this.createSummaryChart();
        }
        else {
            this.createScatterChart();
        }
    }
    createScatterChart() {
        const canvas = this.container.querySelector('#sales-chart-canvas');
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const { datasets, minDate, maxDate, minY, maxY, clippedCount } = this.prepareChartData();
        this.updateClipNote(clippedCount, maxY);
        this.chart = new Chart(ctx, {
            type: 'scatter',
            data: { datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'nearest',
                    intersect: true,
                },
                scales: {
                    x: {
                        type: 'time',
                        min: minDate.getTime(),
                        max: maxDate.getTime(),
                        time: {
                            unit: this.getTimeUnit(),
                            displayFormats: {
                                month: 'MMM yyyy',
                                quarter: 'MMM yyyy',
                                year: 'yyyy',
                            },
                        },
                        grid: {
                            color: CHART_COLORS.gridLines,
                        },
                        ticks: {
                            maxTicksLimit: 8,
                        },
                    },
                    y: {
                        // maxY is already the headroomed ceiling - see
                        // computeYCeiling/prepareChartData - not a plain data max, so
                        // no extra multiplier here.
                        min: Math.max(0, minY * 0.9),
                        max: maxY,
                        grid: {
                            color: CHART_COLORS.gridLines,
                        },
                        ticks: {
                            callback: (value) => {
                                if (this.filters.chartMode === 'pricePerSqft') {
                                    return `$${Number(value).toLocaleString()}`;
                                }
                                return formatPrice(Number(value));
                            },
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const dataPoint = context.raw;
                                if (!dataPoint.meta)
                                    return '';
                                return this.buildTooltipLabel(dataPoint.meta, context.dataset.label || '', dataPoint.clipped ?? false);
                            },
                        },
                    },
                },
                onClick: (_event, elements) => {
                    if (elements.length > 0 && this.options.onDataPointClick) {
                        const element = elements[0];
                        const dataPoint = this.chart?.data.datasets[element.datasetIndex]?.data?.[element.index];
                        if (dataPoint?.meta) {
                            this.options.onDataPointClick(dataPoint.meta);
                        }
                    }
                },
            },
        });
    }
    /**
     * Called on every filter change EXCEPT groupBy (which goes through
     * setGroupBy's destroy+recreate instead, since that's the one filter that
     * changes the Chart.js `type`). Branches so e.g. toggling bedrooms while
     * already in Summary view updates the line chart in place rather than
     * silently doing nothing.
     */
    updateChart() {
        if (this.filters.groupBy !== 'none') {
            this.updateSummaryChart();
        }
        else {
            this.updateScatterChart();
        }
    }
    updateScatterChart() {
        if (!this.chart)
            return;
        const { datasets, minDate, maxDate, minY, maxY, clippedCount } = this.prepareChartData();
        this.updateClipNote(clippedCount, maxY);
        this.chart.data.datasets = datasets;
        // Update scales
        const xScale = this.chart.options.scales?.x;
        const yScale = this.chart.options.scales?.y;
        if (xScale && 'min' in xScale && 'max' in xScale) {
            xScale.min = minDate.getTime();
            xScale.max = maxDate.getTime();
            if ('time' in xScale) {
                xScale.time.unit = this.getTimeUnit();
            }
        }
        if (yScale && 'min' in yScale && 'max' in yScale) {
            // maxY is already the headroomed ceiling - see computeYCeiling.
            yScale.min = Math.max(0, minY * 0.9);
            yScale.max = maxY;
        }
        this.chart.update();
    }
    // Scriptable point styling shared by the Sold/For Sale/Pending datasets -
    // a point whose actualY got clamped to the ceiling (see prepareChartData)
    // renders as a diamond instead of the dataset's normal circle/triangle,
    // so it reads as "cut off, hover for the real value" rather than looking
    // like an ordinary point sitting at the ceiling price. Larger radius too
    // - a diamond at the same size as the circles/triangles around it is
    // easy to miss.
    static clippedAwarePointStyle(normalStyle) {
        return (ctx) => (ctx.raw?.clipped ? 'rectRot' : normalStyle);
    }
    static clippedAwareRadius(normalRadius, clippedRadius) {
        return (ctx) => (ctx.raw?.clipped ? clippedRadius : normalRadius);
    }
    // Details-only footnote (mirrors #partial-period-note's pattern for
    // Summary) - only ever populated from createScatterChart/
    // updateScatterChart, so it's naturally empty/hidden in Summary mode.
    updateClipNote(clippedCount, ceiling) {
        const note = this.container.querySelector('#clip-note');
        if (!note)
            return;
        if (clippedCount === 0) {
            note.style.display = 'none';
            return;
        }
        const ceilingLabel = this.filters.chartMode === 'pricePerSqft'
            ? `$${Math.round(ceiling).toLocaleString()}`
            : formatPrice(ceiling);
        const plural = clippedCount === 1 ? 'point' : 'points';
        note.textContent = `▲ ${clippedCount} ${plural} above ${ceilingLabel} not shown to scale - hover for actual value`;
        note.style.display = '';
    }
    prepareChartData() {
        const filteredSales = filterSales(this.data.sales, this.filters);
        const filteredListings = filterListings(this.data.listings, this.filters);
        const mode = this.filters.chartMode;
        // Raw (unclamped) y per point, computed first so the ceiling below can
        // be derived from the real distribution before any clamping happens.
        const rawSales = filteredSales.map(sale => ({
            x: new Date(sale.saleDate).getTime(),
            actualY: getYValue(sale, mode, true),
            meta: sale,
        }));
        const rawActive = filteredListings
            .filter(l => l.status === 'Active')
            .map(listing => ({
            x: new Date(listing.listDate).getTime(),
            actualY: getYValue(listing, mode, false),
            meta: listing,
        }));
        const rawPending = filteredListings
            .filter(l => l.status === 'Pending')
            .map(listing => ({
            x: new Date(listing.listDate).getTime(),
            actualY: getYValue(listing, mode, false),
            meta: listing,
        }));
        const allActualY = [
            ...rawSales.map(d => d.actualY),
            ...rawActive.map(d => d.actualY),
            ...rawPending.map(d => d.actualY),
        ];
        // See computeYCeiling's comment - a 95th-percentile-based ceiling
        // rather than the plain max, so one outlier sale doesn't compress
        // everything else (and the trend line) into the bottom of the chart.
        const ceiling = computeYCeiling(allActualY);
        const clampToCeiling = (d) => ({
            ...d,
            y: Math.min(d.actualY, ceiling),
            clipped: d.actualY > ceiling,
        });
        const salesData = rawSales.map(clampToCeiling);
        const activeListings = rawActive.map(clampToCeiling);
        const pendingListings = rawPending.map(clampToCeiling);
        const clippedCount = [...salesData, ...activeListings, ...pendingListings].filter(d => d.clipped).length;
        const minDate = getDateCutoff(this.filters.timePeriod);
        const maxDate = new Date();
        const minY = allActualY.length > 0 ? Math.min(...allActualY) : 0;
        const maxY = ceiling;
        const datasets = [];
        // Sales dataset
        datasets.push({
            label: 'Sold',
            data: salesData,
            backgroundColor: CHART_COLORS.sold,
            borderColor: CHART_COLORS.sold,
            pointRadius: SalesChart.clippedAwareRadius(6, 8),
            pointHoverRadius: SalesChart.clippedAwareRadius(8, 10),
            pointStyle: SalesChart.clippedAwarePointStyle('circle'),
        });
        // Active listings dataset
        if (activeListings.length > 0) {
            datasets.push({
                label: 'For Sale',
                data: activeListings,
                backgroundColor: CHART_COLORS.forSale,
                borderColor: CHART_COLORS.forSale,
                pointRadius: SalesChart.clippedAwareRadius(6, 8),
                pointHoverRadius: SalesChart.clippedAwareRadius(8, 10),
                pointStyle: SalesChart.clippedAwarePointStyle('triangle'),
            });
        }
        // Pending listings dataset
        if (pendingListings.length > 0) {
            datasets.push({
                label: 'Pending',
                data: pendingListings,
                backgroundColor: CHART_COLORS.pending,
                borderColor: CHART_COLORS.pending,
                pointRadius: SalesChart.clippedAwareRadius(6, 8),
                pointHoverRadius: SalesChart.clippedAwareRadius(8, 10),
                pointStyle: SalesChart.clippedAwarePointStyle('triangle'),
            });
        }
        // Trend line (if 4+ sales) - fit against actualY (the real, unclamped
        // prices), not the clamped y, so one outlier doesn't bias the slope.
        // Chart.js clips the rendered line to the chart area on its own, so a
        // trend that runs above the ceiling just stops at the top edge rather
        // than needing special-case handling here.
        if (salesData.length >= 4) {
            const regression = calculateLinearRegression(salesData.map(d => ({ x: d.x, y: d.actualY })));
            if (regression) {
                const startX = minDate.getTime();
                const endX = maxDate.getTime();
                datasets.push({
                    label: 'Trend',
                    type: 'line',
                    data: [
                        { x: startX, y: regression.slope * startX + regression.intercept },
                        { x: endX, y: regression.slope * endX + regression.intercept },
                    ],
                    borderColor: CHART_COLORS.trendLine,
                    borderDash: [5, 5],
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: false,
                });
            }
        }
        return { datasets, minDate, maxDate, minY, maxY, clippedCount };
    }
    static MONTH_NAMES = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    /**
     * Summary's grain isn't a user choice - it follows the active time
     * window. A "By Year" view over a 6-month or 1-year window produces at
     * most 2 points, each a partial calendar year, which reads as close to
     * meaningless; by-month gives 6/12 genuinely-comparable points instead. A
     * 5/10-year window the other direction would be 60-120 month points if
     * it used month grain - too dense to read - so it buckets by quarter
     * instead (20/40 points), trading some precision for a chart that still
     * shows real trend shape rather than collapsing to 5-10 year-sized points.
     */
    summaryGrainFor(timePeriod) {
        return timePeriod === '6m' || timePeriod === '1y' ? 'month' : 'quarter';
    }
    /**
     * Bedrooms/stories/timePeriod filters compose naturally with Summary's
     * grouping by reusing filterSales/filterListings on the same underlying
     * data - but chartMode doesn't apply here (the line shows counts/median
     * price, not sale price), and filterSales/filterListings also use
     * chartMode to decide whether a missing-sqft item gets dropped (for
     * '$/Sq Ft' mode). Forcing 'price' here sidesteps that unrelated check
     * rather than teaching the shared filter functions about a view mode
     * they don't otherwise know about.
     */
    prepareSummaryData() {
        const summaryFilters = { ...this.filters, chartMode: 'price' };
        const filteredSales = filterSales(this.data.sales, summaryFilters);
        const filteredListings = filterListings(this.data.listings, summaryFilters);
        const cutoff = getDateCutoff(this.filters.timePeriod);
        // Trailing bucket is always partial (today is always mid-period) -
        // marked with a "*" suffix rather than let it read as a complete
        // period's total (see buildTrailingAwarePointStyle for the matching
        // marker-shape treatment on the line itself). See bucketByQuarter/
        // bucketByMonth's isPartial for the leading side of this (excluded
        // outright, not marked).
        //
        // Sold only - For Sale/Pending are a *current* snapshot (today's open
        // inventory), not a historical count, so bucketing them by listing
        // date and plotting them next to Sold's genuine period totals would
        // misleadingly suggest "this many came on the market in that period"
        // when it's really just "this many of today's active/pending listings
        // happen to have listed then" - almost entirely the most recent 1-2
        // points regardless of window, which reads as a cliff rather than a
        // trend.
        let labels;
        let datasets;
        let buckets;
        // filteredSales/filteredListings are already narrowed to one property
        // type if the user picked one (filterSales/filterListings apply it) -
        // the stacked-area rendering only makes sense, and is only attempted,
        // when 'all' is selected AND the metric is Units Sold (medians don't
        // sum, so Median Price is always a single line - see
        // buildSingleValueDataset). Also gated on showPropertyTypeFilter -
        // without the filter row, there's no way to tell the single-type
        // breakdown apart from "no breakdown", so skip the stacking work too.
        const showStackedByType = this.showPropertyTypeFilter
            && this.filters.propertyType === 'all'
            && this.filters.summaryMetric === 'unitsSold';
        if (this.summaryGrainFor(this.filters.timePeriod) === 'month') {
            const monthBuckets = bucketByMonth(filteredSales, filteredListings, cutoff);
            labels = monthBuckets.map(b => `${SalesChart.MONTH_NAMES[b.month - 1]} ${b.year}${b.isPartial ? ' *' : ''}`);
            datasets = showStackedByType
                ? this.buildPropertyTypeStackedDatasets(monthBuckets)
                : this.buildSingleValueDataset(monthBuckets);
            buckets = monthBuckets;
        }
        else {
            const quarterBuckets = bucketByQuarter(filteredSales, filteredListings, cutoff);
            labels = quarterBuckets.map(b => `Q${b.quarter} ${b.year}${b.isPartial ? ' *' : ''}`);
            datasets = showStackedByType
                ? this.buildPropertyTypeStackedDatasets(quarterBuckets)
                : this.buildSingleValueDataset(quarterBuckets);
            buckets = quarterBuckets;
        }
        // Median Price's y-axis shouldn't run down to $0 - see
        // createSummaryChart's comment - so give it the same "a bit under the
        // real min" treatment the Details scatter plot already uses (see
        // prepareChartData's minY). Thin buckets with no sales at all
        // (medianSalePrice === null) are excluded from this rather than
        // treated as $0, which would otherwise drag the floor down to zero
        // for the one metric this is meant to avoid that for.
        let yMin;
        if (this.filters.summaryMetric === 'medianPrice') {
            const medianValues = buckets.map(b => b.medianSalePrice).filter((v) => v !== null);
            yMin = medianValues.length > 0 ? Math.max(0, Math.min(...medianValues) * 0.9) : undefined;
        }
        return { datasets, labels, yMin };
    }
    // The trailing bucket is always partial (see prepareSummaryData's
    // comment) - rendering its point as a star instead of the usual circle
    // makes that visible on the line itself, not just in the "*" suffixed
    // label text underneath. `buckets` is the same array the dataset's own
    // values came from, so `buckets.length - 1` is always the trailing
    // (and only ever partial) point.
    static trailingAwarePointStyle(buckets, normalRadius, starRadius) {
        const isTrailing = (dataIndex) => buckets[dataIndex]?.isPartial ?? false;
        return {
            pointStyle: (ctx) => (isTrailing(ctx.dataIndex) ? 'star' : 'circle'),
            pointRadius: (ctx) => (isTrailing(ctx.dataIndex) ? starRadius : normalRadius),
        };
    }
    // One dataset per property-type bucket, all sharing the same `stack` id
    // so Chart.js renders them as a single stacked area (fill:true + a
    // stacked y scale - see createSummaryChart) rather than four overlapping
    // lines - color carries the category, height carries the count, same
    // fixed hue order as the Property Type filter buttons. A translucent
    // fill plus an opaque border keeps individual segments readable where
    // they overlap. Takes MonthBucket[] or QuarterBucket[] - both carry
    // soldByType.
    buildPropertyTypeStackedDatasets(buckets) {
        const keys = ['house', 'condo', 'other'];
        return keys.map(key => ({
            label: PROPERTY_TYPE_LABELS[key],
            data: buckets.map(b => b.soldByType[key]),
            borderColor: PROPERTY_TYPE_COLORS[key],
            backgroundColor: hexToRgba(PROPERTY_TYPE_COLORS[key], 0.5),
            stack: 'sold',
            fill: true,
            tension: 0,
            borderWidth: 2,
            ...SalesChart.trailingAwarePointStyle(buckets, 2, 7),
            pointHoverRadius: 4,
        }));
    }
    // The non-stacked case: one color, one series. Used whenever stacking
    // isn't applicable - a specific property type is selected (nothing left
    // to stack; the whole line already IS that type) or Median Price is the
    // metric (a median can't be split into stacked parts the way a count
    // can). Color follows the selected type for visual continuity with its
    // filter button; 'all' falls back to the same green "Sold" has always
    // used. No fill - a plain line reads better than a single-series area
    // once there's no composition to show.
    buildSingleValueDataset(buckets) {
        const color = this.filters.propertyType === 'all' ? CHART_COLORS.sold : PROPERTY_TYPE_COLORS[this.filters.propertyType];
        const label = this.filters.summaryMetric === 'medianPrice' ? 'Median Price' : 'Sold';
        return [{
                label,
                // Median Price keeps null (not 0) for a sale-less bucket - a missing
                // median isn't "$0", and coercing it would also defeat the y-axis
                // floor above by dragging a real data point down to zero. spanGaps
                // bridges the resulting hole so a single quiet bucket doesn't read
                // as a break in the trend.
                data: buckets.map(b => this.filters.summaryMetric === 'medianPrice' ? b.medianSalePrice : b.soldCount),
                spanGaps: true,
                borderColor: color,
                backgroundColor: color,
                fill: false,
                tension: 0,
                borderWidth: 2,
                ...SalesChart.trailingAwarePointStyle(buckets, 3, 8),
                pointHoverRadius: 5,
            }];
    }
    createSummaryChart() {
        const canvas = this.container.querySelector('#sales-chart-canvas');
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const { datasets, labels, yMin } = this.prepareSummaryData();
        this.chart = new Chart(ctx, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                // 'index'/intersect:false so hovering anywhere over a bucket shows
                // every stacked series at that point at once (House/Condo/
                // Other together) - the natural way to read a stacked area's
                // breakdown, and far easier to land on than intersecting a thin
                // line exactly the way bar segments used to allow.
                interaction: {
                    mode: 'index',
                    intersect: false,
                },
                scales: {
                    x: {
                        grid: { display: false },
                    },
                    y: {
                        // `stacked: true` is a no-op visually when there's only one
                        // dataset (the single-line case) - harmless to set
                        // unconditionally rather than branching the chart options on
                        // the same condition prepareSummaryData already branched on.
                        //
                        // Units Sold is a count - 0 is a real, meaningful floor, so it
                        // beginAtZero's as usual. Median Price isn't - a bucket's
                        // median sale price running from, say, $800K to $850K would
                        // get rendered as a nearly flat line against a $0 floor,
                        // hiding the trend entirely. yMin (see prepareSummaryData)
                        // gives price mode the same "a bit under the real min"
                        // floor the Details scatter plot already uses.
                        beginAtZero: yMin === undefined,
                        min: yMin,
                        grid: { color: CHART_COLORS.gridLines },
                        // Reads this.filters.summaryMetric live on every tick render
                        // (not captured once at chart-creation time), so toggling the
                        // Y-Axis metric and calling .update() (see updateSummaryChart)
                        // is enough to reformat ticks - no need to recreate the chart
                        // the way setGroupBy does for the scatter/line switch itself.
                        ticks: {
                            precision: 0,
                            callback: (value) => this.filters.summaryMetric === 'medianPrice' ? formatPrice(Number(value)) : String(value),
                        },
                    },
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            // Only the Units-Sold+All case ever has more than one
                            // dataset (see buildPropertyTypeStackedDatasets) - Median
                            // Price and any single-type selection always take the plain
                            // branch below, since a percent-of-stack reading is
                            // meaningless for either.
                            label: (context) => {
                                const value = context.parsed.y;
                                const displayValue = this.filters.summaryMetric === 'medianPrice' ? formatPrice(value) : String(value);
                                const allDatasets = context.chart.data.datasets;
                                if (allDatasets.length <= 1)
                                    return `${context.dataset.label}: ${displayValue}`;
                                const total = allDatasets.reduce((sum, ds) => sum + (Number(ds.data[context.dataIndex]) || 0), 0);
                                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                                return `${context.dataset.label}: ${displayValue} (${pct}%)`;
                            },
                        },
                    },
                },
            },
        });
    }
    updateSummaryChart() {
        if (!this.chart)
            return;
        const { datasets, labels, yMin } = this.prepareSummaryData();
        this.chart.data.labels = labels;
        this.chart.data.datasets = datasets;
        // Toggling the Y-Axis metric (Units Sold <-> Median Price) doesn't
        // recreate the chart (same Chart.js `type` either way - see
        // setGroupBy's comment for what DOES require that), so the floor
        // picked in createSummaryChart has to be re-applied here too.
        const yScale = this.chart.options.scales?.y;
        if (yScale && 'min' in yScale && 'beginAtZero' in yScale) {
            yScale.min = yMin;
            yScale.beginAtZero = yMin === undefined;
        }
        this.chart.update();
    }
    getTimeUnit() {
        switch (this.filters.timePeriod) {
            case '6m':
                return 'month';
            case '1y':
                return 'month';
            case '5y':
                return 'quarter';
            case '10y':
                return 'year';
            default:
                return 'year';
        }
    }
    buildTooltipLabel(point, datasetLabel, clipped) {
        const lines = [];
        // Address
        const address = point.address || 'Unknown Address';
        const unit = point.unit ? ` #${point.unit}` : '';
        lines.push(`${address}${unit}`);
        if ('salePrice' in point) {
            // Sale
            lines.push(`Sale: ${formatPrice(point.salePrice)}`);
            if (point.listPrice) {
                lines.push(`List: ${formatPrice(point.listPrice)}`);
                const negotiation = getNegotiationPercent(point);
                if (negotiation !== null && Math.abs(negotiation) > 2) {
                    const sign = negotiation > 0 ? '+' : '';
                    lines.push(`Negotiation: ${sign}${negotiation}%`);
                }
            }
            lines.push(`Date: ${formatDate(point.saleDate)}`);
        }
        else {
            // Listing
            lines.push(`${datasetLabel}: ${formatPrice(point.listPrice)}`);
            lines.push(`Listed: ${formatDate(point.listDate)}`);
        }
        // Lines above already show the real (uncapped) price/$-per-sqft off
        // the raw data - this point's marker is just drawn at the Y-axis
        // ceiling, not at its true height, so flag that rather than let the
        // marker's position silently misrepresent it.
        if (clipped) {
            lines.push('▲ Above chart scale - see value above');
        }
        return lines;
    }
}
//# sourceMappingURL=SalesChart.js.map